import { IReadOptions, AjaxHelper } from '@samc/common';
import { WorkflowQbTreeCachedSource } from './WorkflowQbTreeCachedSource';

export const WORKFLOW_TEMPLATE_DOMAIN_LIMITS_DOMAIN_ID = '1';

export interface IDomainLimitItemDto {
  displayName?: string;
  description?: string;
  guid?: string;
  path?: string;
}

export interface IDomainLimitFolderDto {
  subfolders?: IDomainLimitFolderDto[];
  items?: IDomainLimitItemDto[];
  folderGuid?: string;
  folderName?: string;
  folderPath?: string;
}

export interface ILimitPickerFolderRow {
  kind: 'folder';
  key: string;
  text: string;
  level: number;
}

export interface ILimitPickerLimitRow {
  kind: 'limit';
  key: string;
  text: string;
  level: number;
  guid: string;
}

export type ILimitPickerRow = ILimitPickerFolderRow | ILimitPickerLimitRow;

export function filterLimitRowsBySearch(rows: ILimitPickerRow[], search: string): ILimitPickerRow[] {
  const q = search.trim().toLowerCase();
  if (q === '') {
    return rows;
  }
  const keep = new Set<number>();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.kind !== 'limit') {
      continue;
    }
    const textMatch =
      row.text.toLowerCase().includes(q) || row.guid.toLowerCase().includes(q);
    if (!textMatch) {
      continue;
    }
    keep.add(i);
    let minLevel = row.level;
    for (let j = i - 1; j >= 0; j--) {
      const r = rows[j];
      if (r.kind !== 'folder') {
        continue;
      }
      if (r.level < minLevel) {
        keep.add(j);
        minLevel = r.level;
      }
      if (r.level === 0) {
        break;
      }
    }
  }
  return rows.filter((_, i) => keep.has(i));
}

export function templateTreeItemLabel(
  item: IDomainLimitItemDto,
  guidFallback: string
): string {
  const displayName = item.displayName?.trim() || item.description?.trim();
  return displayName && displayName.length > 0 ? displayName : guidFallback;
}

/**
 * @param omitFolderRow When true, the folder row for this node is skipped (used for the API root only).
 */
export function flattenDomainLimitsTree(
  node: IDomainLimitFolderDto,
  level: number,
  out: ILimitPickerRow[],
  omitFolderRow = false,
): void {
  const path = node.folderPath ?? '';
  const name = node.folderName ?? path;
  if (!omitFolderRow) {
    out.push({ kind: 'folder', key: path || name, text: name, level });
  }
  const itemLevel = omitFolderRow ? level : level + 1;
  for (const item of node.items ?? []) {
    const g = item.guid?.trim();
    if (!g) {
      continue;
    }
    const guid = g.toUpperCase();
    out.push({
      kind: 'limit',
      key: guid,
      text: templateTreeItemLabel(item, guid),
      level: itemLevel,
      guid,
    });
  }
  const childFolderBaseLevel = omitFolderRow ? level : level + 1;
  for (const sub of node.subfolders ?? []) {
    flattenDomainLimitsTree(sub, childFolderBaseLevel, out, false);
  }
}

export class DomainLimitsSource extends WorkflowQbTreeCachedSource<ILimitPickerRow> {
  private readonly _helper: AjaxHelper;

  public constructor(
    apiRoot: string | undefined,
    verificationToken: string | null,
    jwt: string | null,
    private readonly _domainId: string = WORKFLOW_TEMPLATE_DOMAIN_LIMITS_DOMAIN_ID
  ) {
    super();
    this._helper = new AjaxHelper(apiRoot ?? '', verificationToken ?? null, null, jwt ?? null);
  }

  public async loadData(options: IReadOptions, abortController: AbortController): Promise<ILimitPickerRow[]> {
    const root = await this._helper.getData<IDomainLimitFolderDto | null>(
      '/api/DomainLimit/GetAll',
      { domainId: this._domainId, tree: 1 },
      { signal: abortController.signal }
    );
    if (!root) {
      return [];
    }
    const rows: ILimitPickerRow[] = [];
    flattenDomainLimitsTree(root, 0, rows, true);
    return rows;
  }
}
